/**
 * aiDraft — what the webinar form sends to "Fill with AI", and how the draft
 * that comes back is merged into the form.
 *
 * The merge is deliberately conservative, because the admin reviews the result
 * rather than re-typing it:
 *   - it never touches what AI cannot know or should not decide: whether the
 *     event is listed, its page address (links may already be shared), images,
 *     branding, the registration mode, attendee permissions, recording;
 *   - a list is replaced only by a non-empty one, so a lazy reply cannot wipe
 *     outcomes or FAQs the admin wrote by hand;
 *   - a speaker the page already has keeps their photo and LinkedIn link;
 *   - the host and the linked course are filled only when empty, and only on
 *     an exact name match against what this admin can actually pick;
 *   - a live session's schedule is never moved.
 */

import { fromLocalInput, toLocalInput } from "./eventForm";

const normName = (s) => String(s || "")
  .toLowerCase()
  .normalize("NFKD")
  .replace(/[̀-ͯ]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The request body's `current`, `settings` and `context`, from the form as it stands. */
export const aiRequestFromForm = (form, { instructors = [], courses = [] } = {}) => {
  const w = form.website || {};
  const names = new Map(instructors.map((i) => [String(i._id), i.name]));
  return {
    current: {
      title: form.title,
      description: form.description,
      eventType: form.eventType,
      scheduledStartAt: fromLocalInput(form.scheduledStartAt),
      scheduledEndAt: fromLocalInput(form.scheduledEndAt),
      website: {
        summary: w.summary,
        highlights: w.highlights,
        audience: w.audience,
        agenda: w.agenda,
        speakers: (w.speakers || []).map(({ name, title, company, bio }) => ({ name, title, company, bio })),
        faqs: w.faqs,
      },
      customQuestions: (form.customQuestions || []).map((q) => ({ label: q.label })),
    },
    settings: {
      certificate: form.certificate,
      reminderOffsets: form.reminderOffsets,
      recordingShared: !!form.recording?.enabled && form.recording?.visibility === "attendees",
      hasWhatsAppGroup: !!(form.whatsappGroupUrl || "").trim(),
      collectEmail: form.collectEmail !== false,
    },
    context: {
      hosts: [form.instructorId, ...(form.coHostInstructorIds || [])]
        .map((id) => names.get(String(id)))
        .filter(Boolean),
      courses: courses.map((c) => c.name).filter(Boolean).slice(0, 40),
    },
    timezone: form.timezone,
  };
};

/** Does the form hold anything AI would build on? Changes what the prompt box asks for. */
export const formHasContent = (form) => {
  const w = form.website || {};
  return Boolean(
    (form.title || "").trim()
    || (form.description || "").trim()
    || (w.summary || "").trim()
    || (w.highlights || []).length
    || (w.faqs || []).length
    || (w.agenda || []).length
  );
};

const oneMatch = (list, name, key = "name") => {
  const want = normName(name);
  if (!want) return null;
  const hits = list.filter((x) => normName(x[key]) === want);
  return hits.length === 1 ? hits[0] : null;
};

/**
 * Merge a draft into the form.
 *
 * @returns {{ form: object, changes: string[] }} `changes` names what was filled,
 *          in the order the form shows it — the admin's review list.
 */
export const applyDraft = (form, draft, {
  instructors = [], courses = [], scheduleLocked = false,
} = {}) => {
  const next = { ...form, website: { ...form.website } };
  const changes = [];
  const d = draft || {};
  const dw = d.website || {};

  if (d.title && d.title !== form.title) { next.title = d.title; changes.push("Title"); }
  if (d.eventType && d.eventType !== form.eventType) { next.eventType = d.eventType; changes.push("Event type"); }
  if (d.description && d.description !== form.description) { next.description = d.description; changes.push("Description"); }

  if (d.schedule && !scheduleLocked) {
    next.scheduledStartAt = toLocalInput(d.schedule.scheduledStartAt);
    next.scheduledEndAt = toLocalInput(d.schedule.scheduledEndAt);
    if (d.timezone) next.timezone = d.timezone;
    changes.push("Date and time");
  }
  if (d.maxAttendees) { next.maxAttendees = d.maxAttendees; changes.push(`Seats: ${d.maxAttendees}`); }

  // The host is filled only when nobody is assigned yet — and only on an
  // exact, unambiguous name. Choosing a host grants them the room.
  if (!form.instructorId) {
    const host = (dw.speakers || []).map((sp) => oneMatch(instructors, sp.name)).find(Boolean);
    if (host) { next.instructorId = String(host._id); changes.push(`Speaker: ${host.name}`); }
  }
  if (!form.courseId && d.relatedCourse) {
    const course = oneMatch(courses, d.relatedCourse);
    if (course) { next.courseId = String(course._id); changes.push(`Linked course: ${course.name}`); }
  }

  if (dw.summary) { next.website.summary = dw.summary; changes.push("Summary"); }
  if (dw.highlights?.length) { next.website.highlights = dw.highlights; changes.push(plural(dw.highlights.length, "outcome")); }
  if (dw.audience?.length) { next.website.audience = dw.audience; changes.push(plural(dw.audience.length, "audience")); }

  if (dw.speakers?.length) {
    const existing = new Map((form.website?.speakers || []).map((sp) => [normName(sp.name), sp]));
    next.website.speakers = dw.speakers.map((sp) => {
      const had = existing.get(normName(sp.name)) || {};
      return {
        name: sp.name,
        title: sp.title || had.title || "",
        company: sp.company || had.company || "",
        bio: sp.bio || had.bio || "",
        // Only the admin supplies these; a draft never replaces them.
        linkedinUrl: had.linkedinUrl || sp.linkedinUrl || "",
        photoUrl: had.photoUrl || "",
        photoKey: had.photoKey || "",
      };
    });
    changes.push(plural(dw.speakers.length, "speaker"));
  }
  if (dw.agenda?.length) { next.website.agenda = dw.agenda; changes.push(`Agenda (${plural(dw.agenda.length, "item")})`); }
  if (dw.faqs?.length) { next.website.faqs = dw.faqs; changes.push(plural(dw.faqs.length, "FAQ")); }

  if (Array.isArray(d.customQuestions)) {
    // Registrations already store answers under a question's key, so an
    // existing question keeps its key when the draft keeps its label.
    const byLabel = new Map((form.customQuestions || []).map((q) => [normName(q.label), q]));
    const used = new Set();
    next.customQuestions = d.customQuestions.map((q) => {
      const had = byLabel.get(normName(q.label));
      let key = had?.key || q.key;
      for (let n = 2; used.has(key); n += 1) key = `${q.key}_${n}`;
      used.add(key);
      return { ...q, key, optionsText: (q.options || []).join(", ") };
    });
    changes.push(plural(next.customQuestions.length, "registration question"));
  }

  if (d.certificate) {
    next.certificate = { ...form.certificate, ...d.certificate };
    changes.push(d.certificate.enabled ? `Certificate at ${d.certificate.minAttendancePct}% attendance` : "Certificate off");
  }
  if (Array.isArray(d.reminderOffsets)) { next.reminderOffsets = d.reminderOffsets; changes.push("Reminders"); }
  if (d.whatsappGroupUrl) { next.whatsappGroupUrl = d.whatsappGroupUrl; changes.push("WhatsApp group link"); }

  return { form: next, changes };
};
